import type { UseQueryResult } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from "lucide-react";
import React from "react";

import {
  expenseDocColor,
  expenseDocTitle,
  TURNOVER_DAYS,
  type TurnoverCategory,
  type TurnoverCounterparty,
  type TurnoverDays,
  type TurnoverItem,
  type TurnoverMetrics,
  type TurnoverSummary,
} from "@/api/warehouse_turnover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtTime, ruNum } from "@/lib/format";
import { LoadState } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";
import {
  docBreakdown,
  docShares,
  filterItems,
  fmtDayMonth,
  fmtDays,
  fmtQtyInt,
  fmtRub,
  ITEMS_INITIAL_LIMIT,
  NO_EXPENSE_HINT,
  nextSort,
  SEARCH_MIN_ITEMS,
  sortRows,
  type SortState,
} from "@/lib/warehouse-turnover";

import { DashboardWarnings } from "./DashboardWarnings";

type CategoryKey =
  | "name"
  | "stock_sum"
  | "avg_stock_sum"
  | "expense_sum"
  | "turnover_days"
  | "cover_days"
  | "no_movement_stock_sum";

type ItemKey =
  | "article"
  | "name"
  | "stock_qty"
  | "stock_sum"
  | "avg_stock_qty"
  | "avg_stock_sum"
  | "expense_qty"
  | "expense_sum"
  | "turnover_days"
  | "cover_days";

const CATEGORY_TEXT: ReadonlySet<CategoryKey> = new Set(["name"]);
const ITEM_TEXT: ReadonlySet<ItemKey> = new Set(["article", "name"]);

/** Период при входе на вкладку — выбор экрана, не настройка. */
export const INITIAL_TURNOVER_DAYS: TurnoverDays = 90;

/** Подпись шапки дашборда, когда открыта оборачиваемость: организация,
 *  период и время расчёта. Подпись — к тому, что НА ЭКРАНЕ: при ошибке там
 *  `LoadState`, и время прошлой удачной загрузки врало бы. */
export function turnoverSubtitle(q: UseQueryResult<TurnoverSummary>): string {
  const data = q.data;
  if (!data || q.error) return "Живые данные из 1С";
  return [
    data.organization.name,
    `период ${fmtDayMonth(data.period_from)}–${fmtDayMonth(data.period_to)}`,
    `данные на ${fmtTime(data.as_of) || data.as_of}`,
  ].join(" · ");
}

/**
 * Вкладка «Оборачиваемость» дашборда склада: запасы организации по
 * категориям номенклатуры с раскрытием до позиций.
 *
 * Запрос живёт в странице (`WarehouseDashboardPage`): шапка с временем и
 * «Обновить» общая, а грузится он только при открытой вкладке. Запрос
 * холодный (1С живьём, 180 дней ≈ несколько секунд): `staleTime` 5 минут,
 * период — в ключе запроса, переключение 30/90/180 не смешивает ответы.
 */
export function TurnoverTab({
  q,
  days,
  onDaysChange,
}: {
  q: UseQueryResult<TurnoverSummary>;
  days: TurnoverDays;
  onDaysChange: (days: TurnoverDays) => void;
}): React.ReactElement {
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const toggle = (ref: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });

  const [sort, setSort] = React.useState<SortState<CategoryKey> | null>(null);
  const onSort = (key: CategoryKey) =>
    setSort((cur) => nextSort(cur, key, CATEGORY_TEXT.has(key)));

  const data = q.data;
  const categories = React.useMemo(
    () => (data ? sortRows(data.categories, sort, (c, k) => c[k]) : []),
    [data, sort],
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div role="group" aria-label="Период" className="flex">
          {TURNOVER_DAYS.map((d, i) => (
            <Button
              key={d}
              size="sm"
              variant={d === days ? "default" : "outline"}
              aria-pressed={d === days}
              onClick={() => onDaysChange(d)}
              className={cn(
                "h-7 px-2.5 text-xs",
                i > 0 && "-ml-px rounded-l-none",
                i < TURNOVER_DAYS.length - 1 && "rounded-r-none",
              )}
            >
              {d} дн
            </Button>
          ))}
        </div>
        {data && !q.error && <DocLegend byDoc={data.totals.expense_by_doc} />}
      </div>

      {q.isLoading || q.error ? (
        <LoadState
          isLoading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          className="flex flex-col items-center justify-center gap-3 p-8 text-center"
        />
      ) : (
        data && (
          <>
            <DashboardWarnings warnings={data.warnings} />
            <CounterpartyStock data={data} />

            {/* Скролл — у таблицы, не у страницы: шапка и плашка остаются на месте. */}
            <div className="overflow-x-auto rounded-lg border bg-white">
              <table className="w-full min-w-[1040px] table-fixed text-[13px] leading-5">
                <colgroup>
                  <col />
                  <col className="w-[112px]" />
                  <col className="w-[112px]" />
                  <col className="w-[120px]" />
                  <col className="w-[76px]" />
                  <col className="w-[76px]" />
                  <col className="w-[200px]" />
                </colgroup>
                <thead className="border-b bg-slate-50">
                  <tr className="text-left text-muted-foreground">
                    <SortTh k="name" sort={sort} onSort={onSort} className="pl-3">
                      Категория
                    </SortTh>
                    <SortTh k="stock_sum" sort={sort} onSort={onSort} right title="Остаток сейчас, ₽">
                      Ост. ₽
                    </SortTh>
                    <SortTh
                      k="avg_stock_sum"
                      sort={sort}
                      onSort={onSort}
                      right
                      title="Средний остаток за период, ₽"
                    >
                      Ср. ост. ₽
                    </SortTh>
                    <SortTh
                      k="expense_sum"
                      sort={sort}
                      onSort={onSort}
                      right
                      title="Расход за период, ₽; полоска — доли документов"
                    >
                      Расход ₽
                    </SortTh>
                    <SortTh
                      k="turnover_days"
                      sort={sort}
                      onSort={onSort}
                      right
                      title="Оборачиваемость: средний остаток / среднедневной расход, дней"
                    >
                      Обор., дн
                    </SortTh>
                    <SortTh
                      k="cover_days"
                      sort={sort}
                      onSort={onSort}
                      right
                      title="Запас: текущий остаток / среднедневной расход, дней"
                    >
                      Запас, дн
                    </SortTh>
                    <SortTh
                      k="no_movement_stock_sum"
                      sort={sort}
                      onSort={onSort}
                      right
                      className="pr-3"
                      title="Позиции с остатком, но без расхода за период: сколько и на сколько ₽"
                    >
                      Нет движения
                    </SortTh>
                  </tr>
                </thead>
                <tbody>
                  <MetricsRow
                    label="Итого"
                    metrics={data.totals}
                    className="bg-slate-50/80 font-semibold"
                  />
                  {categories.map((c) => (
                    <CategoryRows
                      key={c.ref}
                      category={c}
                      open={expanded.has(c.ref)}
                      onToggle={() => toggle(c.ref)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
    </div>
  );
}

/** Легенда цветов полоски расхода — один раз над таблицей, только документы,
 *  по которым за период что-то ушло. */
function DocLegend({ byDoc }: { byDoc: Record<string, number> }): React.ReactElement | null {
  const parts = docShares(byDoc);
  if (parts.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span>Расход по документам:</span>
      {parts.map((p) => (
        <span key={p.key} className="inline-flex items-center gap-1.5">
          <span className={cn("size-2.5 rounded-sm", expenseDocColor(p.key))} />
          {expenseDocTitle(p.key)}
        </span>
      ))}
    </div>
  );
}

/** Тонкая полоска долей документов под числом расхода; цифры — в `title`.
 *  Лежит в нижнем паддинге ячейки (`absolute`), строке высоты не добавляет. */
function DocBar({ byDoc }: { byDoc: Record<string, number> }): React.ReactElement | null {
  const parts = docShares(byDoc);
  if (parts.length === 0) return null;
  return (
    <div className="absolute bottom-[2px] right-2 flex h-[2px] w-[calc(100%-1rem)] max-w-[72px] gap-px overflow-hidden rounded-full">
      {parts.map((p) => (
        <span
          key={p.key}
          className={cn("h-full", expenseDocColor(p.key))}
          style={{ width: `${p.share * 100}%` }}
        />
      ))}
    </div>
  );
}

function SortTh<K extends string>({
  k,
  sort,
  onSort,
  right,
  className,
  title,
  children,
}: {
  k: K;
  sort: SortState<K> | null;
  onSort: (k: K) => void;
  right?: boolean;
  className?: string;
  /** Полное название колонки — заголовок сокращён, чтобы влезть в строку. */
  title?: string;
  children: React.ReactNode;
}): React.ReactElement {
  const active = sort?.key === k ? sort.dir : null;
  return (
    <th
      aria-sort={active === "asc" ? "ascending" : active === "desc" ? "descending" : "none"}
      title={title}
      className={cn(
        "whitespace-nowrap px-2 py-1.5 text-xs font-medium",
        right && "text-right",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSort(k)}
        className={cn(
          "inline-flex items-center gap-0.5 hover:text-foreground",
          active && "text-foreground",
        )}
      >
        {children}
        {active === "asc" && <ArrowUp className="size-3" />}
        {active === "desc" && <ArrowDown className="size-3" />}
      </button>
    </th>
  );
}

const NUM = "whitespace-nowrap px-2 py-1.5 text-right tabular-nums";

function DaysCell({
  value,
  className,
}: {
  value: number | null;
  className?: string;
}): React.ReactElement {
  return (
    <td className={cn(NUM, className)} title={value === null ? NO_EXPENSE_HINT : undefined}>
      {value === null ? <span className="text-muted-foreground">—</span> : fmtDays(value)}
    </td>
  );
}

/** Ячейки метрик группы (категории или итога) — общие для обеих строк. */
function MetricCells({ metrics: m }: { metrics: TurnoverMetrics }): React.ReactElement {
  const docs = docBreakdown(m.expense_by_doc, (v) => `${fmtRub(v)} ₽`);
  return (
    <>
      <td className={NUM}>{fmtRub(m.stock_sum)}</td>
      <td className={NUM}>{fmtRub(m.avg_stock_sum)}</td>
      <td className={cn(NUM, "relative")} title={docs || undefined}>
        {fmtRub(m.expense_sum)}
        <DocBar byDoc={m.expense_by_doc} />
      </td>
      <DaysCell value={m.turnover_days} />
      <DaysCell value={m.cover_days} />
      <td className={cn(NUM, "pr-3")}>
        {m.no_movement_count > 0 ? (
          <span className="text-amber-700">
            {ruNum.format(m.no_movement_count)} поз.
            <span className="text-xs font-normal text-amber-700/80">
              {" "}
              · {fmtRub(m.no_movement_stock_sum)} ₽
            </span>
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
    </>
  );
}

function MetricsRow({
  label,
  metrics,
  className,
}: {
  label: string;
  metrics: TurnoverMetrics;
  className?: string;
}): React.ReactElement {
  return (
    <tr className={cn("border-b", className)}>
      <td className="truncate px-2 py-1.5 pl-3">
        {label}{" "}
        <span className="text-xs font-normal text-muted-foreground">
          {ruNum.format(metrics.items_count)} поз.
        </span>
      </td>
      <MetricCells metrics={metrics} />
    </tr>
  );
}

function CategoryRows({
  category: c,
  open,
  onToggle,
}: {
  category: TurnoverCategory;
  open: boolean;
  onToggle: () => void;
}): React.ReactElement {
  return (
    <>
      <tr className={cn("border-b hover:bg-slate-50", open && "bg-slate-50")}>
        <td className="px-2 py-1.5 pl-3">
          <button
            type="button"
            aria-expanded={open}
            onClick={onToggle}
            title={c.name}
            className="flex w-full min-w-0 items-center gap-1 text-left"
          >
            {open ? (
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate font-medium">{c.name}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {ruNum.format(c.items_count)} поз.
            </span>
          </button>
        </td>
        <MetricCells metrics={c} />
      </tr>
      {open && (
        <tr className="border-b bg-slate-50/70">
          {/* Вложенность — линией слева, не отступом: ширина нужна числам. */}
          <td colSpan={7} className="py-1.5 pl-3 pr-2">
            <div className="border-l-2 border-slate-300 pl-2">
              <CategoryItems items={c.items} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Рубли у позиции — отдельной колонкой, вторичным цветом (основное — штуки). */
const RUB = cn(NUM, "text-muted-foreground");

/** Позиции раскрытой категории: своя сортировка, поиск (если позиций много),
 *  начальный вывод ограничен — 400+ строк разом тормозят раскрытие. */
function CategoryItems({ items }: { items: TurnoverItem[] }): React.ReactElement {
  const [sort, setSort] = React.useState<SortState<ItemKey> | null>(null);
  const [query, setQuery] = React.useState("");
  const [showAll, setShowAll] = React.useState(false);
  const onSort = (key: ItemKey) =>
    setSort((cur) => nextSort(cur, key, ITEM_TEXT.has(key)));

  const rows = React.useMemo(
    () => sortRows(filterItems(items, query), sort, (i, k) => i[k]),
    [items, query, sort],
  );
  const shown = showAll ? rows : rows.slice(0, ITEMS_INITIAL_LIMIT);

  if (items.length === 0) {
    return <p className="text-muted-foreground">Позиций нет</p>;
  }

  return (
    <div className="space-y-1.5">
      {items.length > SEARCH_MIN_ITEMS && (
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск по артикулу или названию"
          className="h-7 max-w-xs bg-white px-2 py-0 text-xs"
        />
      )}
      <table className="w-full table-fixed rounded-md bg-white text-[13px] leading-5 shadow-[0_0_0_1px_hsl(var(--border))]">
        <colgroup>
          <col className="w-[124px]" />
          <col />
          <col className="w-[72px]" />
          <col className="w-[92px]" />
          <col className="w-[76px]" />
          <col className="w-[92px]" />
          <col className="w-[76px]" />
          <col className="w-[92px]" />
          <col className="w-[68px]" />
          <col className="w-[72px]" />
        </colgroup>
        <thead className="border-b">
          <tr className="text-left text-muted-foreground">
            <SortTh k="article" sort={sort} onSort={onSort} className="py-1 pl-2">
              Артикул
            </SortTh>
            <SortTh k="name" sort={sort} onSort={onSort} className="py-1">
              Товар
            </SortTh>
            <SortTh k="stock_qty" sort={sort} onSort={onSort} right className="py-1" title="Остаток сейчас, шт">
              Ост. шт
            </SortTh>
            <SortTh k="stock_sum" sort={sort} onSort={onSort} right className="py-1" title="Остаток сейчас, ₽">
              Ост. ₽
            </SortTh>
            <SortTh
              k="avg_stock_qty"
              sort={sort}
              onSort={onSort}
              right
              className="py-1"
              title="Средний остаток за период, шт"
            >
              Ср. ост. шт
            </SortTh>
            <SortTh
              k="avg_stock_sum"
              sort={sort}
              onSort={onSort}
              right
              className="py-1"
              title="Средний остаток за период, ₽"
            >
              Ср. ост. ₽
            </SortTh>
            <SortTh
              k="expense_qty"
              sort={sort}
              onSort={onSort}
              right
              className="py-1"
              title="Расход за период, шт; полоска — доли документов"
            >
              Расход шт
            </SortTh>
            <SortTh k="expense_sum" sort={sort} onSort={onSort} right className="py-1" title="Расход за период, ₽">
              Расход ₽
            </SortTh>
            <SortTh
              k="turnover_days"
              sort={sort}
              onSort={onSort}
              right
              className="py-1"
              title="Оборачиваемость: средний остаток / среднедневной расход, дней"
            >
              Обор., дн
            </SortTh>
            <SortTh
              k="cover_days"
              sort={sort}
              onSort={onSort}
              right
              className="py-1 pr-2"
              title="Запас: текущий остаток / среднедневной расход, дней"
            >
              Запас, дн
            </SortTh>
          </tr>
        </thead>
        <tbody>
          {shown.map((i) => {
            const docs = docBreakdown(i.expense_by_doc_qty, (v) => `${ruNum.format(v)} шт`);
            return (
              <tr key={i.nomenclature_ref} className="border-b last:border-0 hover:bg-sky-50/40 [&>td]:py-1">
                <td className="truncate px-2 tabular-nums" title={i.article || undefined}>
                  {i.article || "—"}
                </td>
                <td className="px-2">
                  <div className="flex min-w-0 items-center gap-1.5" title={i.name}>
                    {i.no_movement && (
                      <span
                        className="size-1.5 shrink-0 rounded-full bg-amber-500"
                        title="нет движения за период"
                      >
                        <span className="sr-only">нет движения</span>
                      </span>
                    )}
                    <span className="truncate">{i.name}</span>
                  </div>
                </td>
                <td className={NUM}>{ruNum.format(i.stock_qty)}</td>
                <td className={RUB}>{fmtRub(i.stock_sum)}</td>
                <td className={NUM}>{fmtQtyInt(i.avg_stock_qty)}</td>
                <td className={RUB}>{fmtRub(i.avg_stock_sum)}</td>
                <td className={cn(NUM, "relative")} title={docs || undefined}>
                  {ruNum.format(i.expense_qty)}
                  <DocBar byDoc={i.expense_by_doc_qty} />
                </td>
                <td className={RUB}>{fmtRub(i.expense_sum)}</td>
                <DaysCell value={i.turnover_days} />
                <DaysCell value={i.cover_days} className="pr-2" />
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && <p className="text-muted-foreground">Ничего не найдено</p>}
      {!showAll && rows.length > ITEMS_INITIAL_LIMIT && (
        <Button variant="outline" size="sm" onClick={() => setShowAll(true)}>
          Показать все ({ruNum.format(rows.length)})
        </Button>
      )}
    </div>
  );
}

/** Товар у контрагентов (комиссия, переработка) — компактный сворачиваемый
 *  блок: не склад, в таблицу выше не входит, но и не предупреждение. Свёрнут по
 *  умолчанию; пустой список — блока нет. */
function CounterpartyStock({ data }: { data: TurnoverSummary }): React.ReactElement | null {
  const [open, setOpen] = React.useState(false);
  const rows: TurnoverCounterparty[] = data.counterparty_stock;
  if (rows.length === 0) return null;
  return (
    <section
      aria-label="Товар у контрагентов"
      className="max-w-[640px] rounded-lg border bg-white text-[13px]"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left hover:bg-slate-50"
      >
        {open ? (
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="shrink-0 whitespace-nowrap font-medium text-slate-700">Товар у контрагентов</span>
        <span className="min-w-0 truncate text-muted-foreground" title="комиссия, переработка — не входит в оборачиваемость">
          комиссия, переработка — не входит в оборачиваемость
        </span>
        <span className="ml-auto shrink-0 whitespace-nowrap pl-2 tabular-nums text-slate-700">
          {ruNum.format(data.counterparty_stock_qty)} шт · {fmtRub(data.counterparty_stock_sum)} ₽
        </span>
      </button>
      {open && (
        <table className="w-full table-fixed border-t leading-5">
          <colgroup>
            <col />
            <col className="w-[96px]" />
            <col className="w-[120px]" />
          </colgroup>
          <thead className="bg-slate-50 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-1 text-left font-medium">Контрагент</th>
              <th className="px-2 py-1 text-right font-medium">Сейчас шт</th>
              <th className="px-3 py-1 text-right font-medium">Сейчас ₽</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.ref} className="border-t">
                <td className="truncate px-3 py-1" title={c.in_snapshots_only ? `${c.name} — сейчас ноль, был в прошлых срезах` : c.name}>
                  {c.name}
                  {c.in_snapshots_only && (
                    <span className="ml-1.5 text-xs text-muted-foreground">был в прошлых срезах</span>
                  )}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">{ruNum.format(c.stock_qty)}</td>
                <td className="px-3 py-1 text-right tabular-nums text-muted-foreground">
                  {fmtRub(c.stock_sum)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
